/**
 * MonitorDisplay — monitor multiparamétrico hospitalario.
 *
 * Pantalla que replica la UI de un monitor Dräger/Philips/GE:
 *   - ECG derivación II con trazado en vivo (scroll izquierda)
 *   - Curva pletismográfica SpO₂ (sincrónica con FC)
 *   - Valores numéricos grandes estilo clínico: FC verde, SpO₂ azul,
 *     NIBP rojo (sistólica/diastólica, MAP), FR amarillo, Temp blanca
 *   - Alarmas visuales + audibles cuando un vital sale de rango
 *   - Barra inferior con mensajes de alarma activos
 *   - Botón "Silenciar" (60s) estándar
 *
 * Toma datos del Patient actual y del DeteriorationEngine para que los
 * valores cambien en vivo según progresa el caso. Mientras el alumno mira
 * el monitor, se siente en una sala de urgencias real, no explorando un
 * grafo académico.
 */

import type { Patient } from '../medical/Patient';
import type { ClinicalNodeData } from '../types/medical';
import state from '../core/state';
import { authService } from '../auth/AuthService';
import { ECGGenerator, PlethGenerator, type ECGRhythm } from './ECGGenerator';
import { alarmSound, type AlarmLevel } from './AlarmSound';
import { patientVoice, type PatientVoiceTurn } from '../interaction/PatientVoice';
import { AUSC_ZONES, localFindingFor, type AuscZone } from './AuscultationMap';
import { clinicalGestures, type ClinicalGestureEvent } from '../interaction/ClinicalGestures';
import { physiologyEngine, DRUG_CATALOG } from '../medical/PhysiologyEngine';
import { sessionRecorder } from '../education/SessionRecorder';
import { sessionDebrief } from '../education/SessionDebrief';
import { liveSession } from '../live/LiveSession';

/** Room de sesión live: el backend lo deriva del usuario (live-{teacher_id}
 * para alumnos, live-{id} para profesor, "global-live" como fallback). */
function liveRoomForCurrentUser(): string {
  return authService.currentUser?.live_room ?? 'global-live';
}

interface ClinicalActionResponse {
  narrative: string;
  vitals_delta: Record<string, number> | null;
  triage_reclassify: string | null;
  teaching_note: string | null;
}
interface NarrativeEventResponse {
  kind: 'patient' | 'nurse' | 'family' | 'none';
  message: string;
  urgency: 'info' | 'warning' | 'critical';
}

/** Catálogo de acciones clínicas que el alumno puede tomar. */
const CLINICAL_ACTIONS: Array<{ id: string; icon: string; label: string; detail?: string }> = [
  { id: 'auscultar-torax', icon: '👂', label: 'Auscultar tórax' },
  { id: 'auscultar-abdomen', icon: '👂', label: 'Auscultar abdomen' },
  { id: 'palpar-abdomen', icon: '🤲', label: 'Palpar abdomen' },
  { id: 'evaluar-pupilas', icon: '👁️', label: 'Evaluar pupilas' },
  { id: 'pulso-perif', icon: '🫱', label: 'Pulso periférico' },
  { id: 'oxigeno', icon: '🫁', label: 'Administrar O₂', detail: 'Cánula 3L/min' },
  { id: 'via-ev', icon: '💉', label: 'Vía EV + SF', detail: 'Suero fisiológico 500ml' },
  { id: 'ecg-12', icon: '📈', label: 'Ordenar ECG 12D' },
  { id: 'rx-torax', icon: '📷', label: 'Rx tórax portátil' },
  { id: 'aspirina', icon: '💊', label: 'Aspirina 300mg', detail: 'VO' },
  { id: 'interconsulta', icon: '📞', label: 'Solicitar interconsulta' },
];

/** Nombres chilenos frecuentes para pacientes ficticios — selección
 * determinística por hash de case_id. Cubre m/f/other para no excluir.
 * La meta es inmersión OSCE (profe: "¿qué le dirías a Don Juan?"). */
const FIRST_NAMES_M = ['Jorge', 'Juan', 'Pedro', 'Carlos', 'Luis', 'Miguel', 'Ricardo', 'Sergio', 'Hernán', 'Manuel'];
const FIRST_NAMES_F = ['María', 'Rosa', 'Carmen', 'Ana', 'Patricia', 'Sandra', 'Marta', 'Claudia', 'Paulina', 'Isabel'];
const LAST_NAMES = ['Pérez', 'Soto', 'González', 'Rojas', 'Muñoz', 'Torres', 'Ramírez', 'Vargas', 'Castro', 'Herrera'];
function generateFictionalName(caseId: string, sex: string): string {
  let h = 0;
  for (let i = 0; i < caseId.length; i++) h = (h * 31 + caseId.charCodeAt(i)) >>> 0;
  const firsts = sex === 'female' ? FIRST_NAMES_F : FIRST_NAMES_M;
  const first = firsts[h % firsts.length];
  const last = LAST_NAMES[(h >> 5) % LAST_NAMES.length];
  return `${first} ${last}`;
}

const CONTAINER_ID = 'cgt-monitor';
const STYLE_ID = 'cgt-monitor-styles';

interface VitalRead {
  code: string;
  display: string;
  value: number | null;
  unit: string;
  refLow?: number;
  refHigh?: number;
  /** Nivel de alarma derivado del valor vs referencias. */
  level: AlarmLevel;
  /** Color canónico en monitor real. */
  color: string;
}

export class MonitorDisplay {
  private root: HTMLElement | null = null;
  private patient: Patient | null = null;
  private ecgCanvas: HTMLCanvasElement | null = null;
  private plethCanvas: HTMLCanvasElement | null = null;
  private rafId: number | null = null;
  private lastFrame = 0;
  /** Unsubscriber del detector de gestos clínicos (Sprint 3). */
  private clinicalGesturesOff: (() => void) | null = null;
  /** Timer del snapshot periódico de vitales para el feed live (Sprint 5). */
  private vitalsSnapshotTimer: number | null = null;
  private ecgGen = new ECGGenerator();
  private plethGen = new PlethGenerator();
  private ecgBuffer: number[] = [];
  private plethBuffer: number[] = [];
  /** Cantidad de muestras que mostramos a lo ancho (equivale a ~6-8 segundos). */
  private readonly SAMPLES = 600;

  mount(container: HTMLElement): void {
    this.injectStyles();
    if (this.root) return;
    const el = document.createElement('div');
    el.id = CONTAINER_ID;
    el.innerHTML = this.template();
    container.appendChild(el);
    this.root = el;

    // Clase en body: ocultamos TODO lo que pertenece al modo grafo (tooltips
    // de nodos, HUD, leyenda, badge de cámara) mientras el monitor está
    // activo. El alumno ve SOLO el monitor — como en un box clínico real.
    document.body.classList.add('cgt-monitor-active');

    this.ecgCanvas = el.querySelector<HTMLCanvasElement>('[data-ecg]');
    this.plethCanvas = el.querySelector<HTMLCanvasElement>('[data-pleth]');

    // Primear audio al primer click
    el.addEventListener('click', () => alarmSound.prime(), { once: true });

    el.querySelector<HTMLButtonElement>('[data-silence]')?.addEventListener('click', () => {
      alarmSound.muteFor(60);
      this.updateSilenceButton(true);
    });
    el.querySelector<HTMLButtonElement>('[data-close-monitor]')?.addEventListener('click', () => {
      // Sprint 5: lanzar debrief IA antes de cerrar, si hubo eventos.
      const rec = sessionRecorder.get();
      const app = (window as unknown as { app?: { closeMonitor?: () => void } }).app;
      if (rec && rec.events.length >= 3) {
        void sessionDebrief.show();
        // No cerramos el monitor hasta que el alumno cierre el debrief:
        // así puede revisar el score mientras el box sigue visible de fondo.
        // Pero permitimos cerrar manualmente si quiere con un 2do click
        // (el card del debrief está por encima visualmente).
      } else {
        app?.closeMonitor?.();
      }
    });

    el.querySelector<HTMLButtonElement>('[data-auscultate]')?.addEventListener('click', () => {
      this.triggerAuscultation('click');
    });

    el.querySelector<HTMLButtonElement>('[data-debrief]')?.addEventListener('click', () => {
      void sessionDebrief.show();
    });

    // Sensibilidad de gestos: persistida en localStorage; ajusta thresholds
    // de RCP/defib/pulse en caliente (sin reload).
    const sensSel = el.querySelector<HTMLSelectElement>('[data-sens]');
    if (sensSel) {
      sensSel.value = clinicalGestures.getSensitivity();
      sensSel.addEventListener('change', () => {
        const v = sensSel.value as 'low' | 'medium' | 'high';
        clinicalGestures.setSensitivity(v);
        this.appendLog('system', `🎚️ Sensibilidad de gestos: ${v}`);
      });
    }

    // Voz del paciente (push-to-talk)
    const voiceBtn = el.querySelector<HTMLButtonElement>('[data-voice]');
    if (voiceBtn) {
      if (!patientVoice.isSupported()) {
        voiceBtn.disabled = true;
        voiceBtn.title = 'Reconocimiento de voz no soportado en este navegador';
      } else {
        if (this.patient) patientVoice.setPatient(this.patient);
        patientVoice.onTurnAdded((turn) => this.onVoiceTurn(turn));
        patientVoice.onState((s) => this.onVoiceState(s, voiceBtn));
        // Push-to-talk: mientras apretás el botón, escucha
        voiceBtn.addEventListener('mousedown', () => patientVoice.startListening());
        voiceBtn.addEventListener('mouseup', () => patientVoice.stopListening());
        voiceBtn.addEventListener('mouseleave', () => patientVoice.stopListening());
        voiceBtn.addEventListener('touchstart', (e) => {
          e.preventDefault();
          patientVoice.startListening();
        });
        voiceBtn.addEventListener('touchend', (e) => {
          e.preventDefault();
          patientVoice.stopListening();
        });
      }
    }

    // Gestos con la mano: suscribirse al bus de estado de la mano. Si detectamos
    // mano abierta sostenida >1.5s frente al monitor (palma abierta, quieta),
    // disparamos auscultación. Esto es LO QUE EL DOC VALORÓ: la cámara detecta
    // el gesto clínico real y activa cosas en el monitor — un fantoma físico
    // no puede hacer esto.
    this.startHandGestureLoop();

    // Sprint 3: gestos de procedimiento (RCP/defib/pulse). El detector se
    // alimenta en main.ts (handController.onHandsDetected); acá solo nos
    // suscribimos y traducimos eventos → acciones clínicas con consecuencias.
    this.clinicalGesturesOff?.();
    this.clinicalGesturesOff = clinicalGestures.onGesture(ev => this.onClinicalGesture(ev));

    // Acciones clínicas vía mouse (alternativa universal al gesto)
    el.querySelectorAll<HTMLButtonElement>('[data-action]').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.action!;
        const label = btn.dataset.actionLabel!;
        const detail = btn.dataset.actionDetail;
        this.performClinicalAction(id, label, detail);
      });
    });

    // Sprint 4: drogas del catálogo. Cada click administra la droga y el
    // motor farmacodinámico aplica los deltas en vitales a lo largo del
    // tiempo (rampa → plateau → decay). Además logueamos y pedimos una
    // narrativa IA breve para reforzar el rationale clínico.
    el.querySelectorAll<HTMLButtonElement>('[data-drug]').forEach(btn => {
      btn.addEventListener('click', () => this.administerDrug(btn.dataset.drug!));
    });

    // Narrativa IA que corre en background: cada 25-35s la IA puede
    // generar un evento contextual (el paciente habla, enfermería reporta,
    // etc.). Esto replica la realidad de un box clínico y es exactamente
    // lo que un fantoma no puede hacer.
    this.scheduleNarrativeTick();
    this.startedAt = Date.now();

    this.resizeCanvases();
    window.addEventListener('resize', () => this.resizeCanvases());
    this.lastFrame = performance.now();
    this.loop();
  }

  unmount(): void {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    if (this.handGestureRaf !== null) cancelAnimationFrame(this.handGestureRaf);
    if (this.narrativeTimer !== null) window.clearTimeout(this.narrativeTimer);
    this.rafId = null;
    this.handGestureRaf = null;
    this.narrativeTimer = null;
    this.clinicalGesturesOff?.();
    this.clinicalGesturesOff = null;
    if (this.vitalsSnapshotTimer !== null) window.clearInterval(this.vitalsSnapshotTimer);
    this.vitalsSnapshotTimer = null;
    try { liveSession.send({ type: 'student.caseEnd' }); } catch { /* noop */ }
    liveSession.disconnect();
    this.actionHistory = [];
    this.vitalsOverride = {};
    alarmSound.setLevel('off');
    document.body.classList.remove('cgt-monitor-active');
    this.root?.remove();
    this.root = null;
  }

  setPatient(patient: Patient | null): void {
    this.patient = patient;
    this.ecgBuffer = [];
    this.plethBuffer = [];
    physiologyEngine.reset();
    patientVoice.setPatient(patient);
    if (patient) {
      const dxNode = patient.data.nodes.find(n => n.type === 'diagnosis');
      sessionRecorder.start(
        patient.data.id,
        dxNode?.concept.display ?? null,
        patient.data.chiefComplaint ?? null,
      );
      // Sprint 5 multi-device: conectar al room global y publicar inicio.
      // El profesor que esté con el dashboard "En vivo" abierto verá el
      // feed del alumno en tiempo real (acciones, drogas, gestos, voz).
      try {
        liveSession.connect(liveRoomForCurrentUser());
        // Pequeño delay para que el WS esté abierto antes del primer publish
        setTimeout(() => {
          liveSession.send({
            type: 'student.caseStart',
            caseId: patient.data.id,
            diagnosis: dxNode?.concept.display ?? null,
            chiefComplaint: patient.data.chiefComplaint ?? null,
          });
        }, 600);
        // Snapshot de vitales cada 15s al feed live: el profesor ve la
        // evolución sin necesidad de que el alumno reporte cada cambio.
        if (this.vitalsSnapshotTimer !== null) window.clearInterval(this.vitalsSnapshotTimer);
        // 8s base: balance entre frescura del feed (peak de adrenalina ~30-45s)
        // y carga de WS. Drogas también disparan snapshots inmediatos +30s.
        this.vitalsSnapshotTimer = window.setInterval(() => this.broadcastVitalsSnapshot(), 8000);
      } catch (e) {
        console.warn('[MonitorDisplay] live connect falló:', e);
      }
    }
  }

  /** Refresca el HUD de detección de gestos clínicos en tiempo real. */
  private updateGestureHud(): void {
    const hud = this.root?.querySelector<HTMLElement>('[data-gesthud]');
    if (!hud) return;
    const s = clinicalGestures.getDetectionState();
    const fillComp = hud.querySelector<HTMLElement>('[data-g-fill="comp"]');
    const fillDefib = hud.querySelector<HTMLElement>('[data-g-fill="defib"]');
    const fillPulse = hud.querySelector<HTMLElement>('[data-g-fill="pulse"]');
    const rateEl = hud.querySelector<HTMLElement>('[data-g-rate]');
    if (fillComp) fillComp.style.width = `${Math.round(s.compressions * 100)}%`;
    if (fillDefib) fillDefib.style.width = `${Math.round(s.defib * 100)}%`;
    if (fillPulse) fillPulse.style.width = `${Math.round(s.pulse * 100)}%`;
    if (rateEl) rateEl.textContent = s.compressionRate > 0 ? String(s.compressionRate) : '—';
    // Solo mostramos el HUD si alguno está progresando: aria-hidden + opacity
    const anyActive = s.compressions > 0 || s.defib > 0 || s.pulse > 0;
    hud.classList.toggle('cgt-mon-gesthud-active', anyActive);
  }

  /** Lee vitales actuales y los emite al feed live (Sprint 5 multi-device). */
  private broadcastVitalsSnapshot(): void {
    if (!this.patient) return;
    try {
      const vitals = this.readVitals();
      const out: Record<string, number | null> = {};
      (['hr', 'sbp', 'dbp', 'spo2', 'rr', 'temp'] as const).forEach(k => {
        out[k] = vitals[k]?.value ?? null;
      });
      liveSession.send({ type: 'student.vitals', vitals: out });
    } catch { /* noop */ }
  }

  private onVoiceTurn(turn: PatientVoiceTurn): void {
    const icon = turn.role === 'student' ? '🩺' : '🗣️';
    const source = turn.role === 'student' ? 'Doctor/a' : 'Paciente';
    const text = `${icon} ${source}: "${turn.text}"`;
    this.appendLog(turn.role === 'student' ? 'action' : 'ai', text);
    sessionRecorder.record(
      turn.role === 'student' ? 'voice-student' : 'voice-patient',
      turn.text,
      turn.emotion ? { emotion: turn.emotion } : undefined,
    );
    liveSession.send({ type: 'student.voice', role: turn.role, text: turn.text });
  }

  private onVoiceState(state: 'idle' | 'listening' | 'thinking' | 'speaking', btn: HTMLButtonElement): void {
    btn.classList.remove('cgt-mon-voice-listening', 'cgt-mon-voice-thinking', 'cgt-mon-voice-speaking');
    switch (state) {
      case 'listening':
        btn.classList.add('cgt-mon-voice-listening');
        btn.textContent = '🔴 Escuchando…';
        break;
      case 'thinking':
        btn.classList.add('cgt-mon-voice-thinking');
        btn.textContent = '⏳ Pensando…';
        break;
      case 'speaking':
        btn.classList.add('cgt-mon-voice-speaking');
        btn.textContent = '🔊 Hablando…';
        break;
      default:
        btn.textContent = '🎤 Hablar';
    }
  }

  private template(): string {
    return `
      <header class="cgt-mon-header">
        <div class="cgt-mon-bed">
          <span class="cgt-mon-bed-label">CAMA</span>
          <span class="cgt-mon-bed-num" data-bed>01</span>
        </div>
        <div class="cgt-mon-patient">
          <strong data-pname>—</strong>
          <div class="cgt-mon-patient-meta">
            <span data-pdemo>—</span>
            <span class="cgt-mon-patient-id" data-pid>—</span>
            <span class="cgt-mon-patient-alergias" data-palergias></span>
          </div>
        </div>
        <div class="cgt-mon-clock-col">
          <div class="cgt-mon-clock" data-clock>--:--</div>
          <div class="cgt-mon-clock-date" data-clockdate>—</div>
        </div>
        <div class="cgt-mon-header-actions">
          <button type="button" class="cgt-mon-action cgt-mon-voice" data-voice title="Mantener presionado para hablarle al paciente (el paciente responde con voz)">🎤 Hablar</button>
          <button type="button" class="cgt-mon-action" data-auscultate title="Auscultar (o levantá la palma frente a la cámara 2s)">👂 Auscultar</button>
          <select class="cgt-mon-sens" data-sens title="Sensibilidad de gestos clínicos (RCP/defib/pulse)">
            <option value="low">Sensibilidad: baja</option>
            <option value="medium" selected>Sensibilidad: media</option>
            <option value="high">Sensibilidad: alta</option>
          </select>
          <button type="button" class="cgt-mon-action" data-debrief title="Ver debrief IA de la sesión actual">📋 Debrief</button>
          <button type="button" class="cgt-mon-silence" data-silence title="Silenciar alarmas 60s">🔕 Silenciar</button>
          <button type="button" class="cgt-mon-close" data-close-monitor title="Volver">← Volver</button>
        </div>
      </header>
      <div class="cgt-mon-palm-hint" data-palm-overlay>
        <div class="cgt-mon-palm-icon">✋</div>
        <div class="cgt-mon-palm-text">Manteniendo palma… <strong>auscultando</strong></div>
        <div class="cgt-mon-palm-bar"><div class="cgt-mon-palm-fill"></div></div>
      </div>
      <div class="cgt-mon-gesthud" data-gesthud aria-hidden="true">
        <div class="cgt-mon-gesthud-row" data-g-row="comp">
          <span class="cgt-mon-gesthud-icon">🫸</span>
          <span class="cgt-mon-gesthud-label">RCP <em data-g-rate>—</em>/min</span>
          <div class="cgt-mon-gesthud-bar"><div class="cgt-mon-gesthud-fill" data-g-fill="comp"></div></div>
        </div>
        <div class="cgt-mon-gesthud-row" data-g-row="defib">
          <span class="cgt-mon-gesthud-icon">⚡</span>
          <span class="cgt-mon-gesthud-label">Defib</span>
          <div class="cgt-mon-gesthud-bar"><div class="cgt-mon-gesthud-fill" data-g-fill="defib"></div></div>
        </div>
        <div class="cgt-mon-gesthud-row" data-g-row="pulse">
          <span class="cgt-mon-gesthud-icon">🫱</span>
          <span class="cgt-mon-gesthud-label">Pulso</span>
          <div class="cgt-mon-gesthud-bar"><div class="cgt-mon-gesthud-fill" data-g-fill="pulse"></div></div>
        </div>
      </div>
      <div class="cgt-mon-grid">
        <section class="cgt-mon-waveform cgt-mon-ecg">
          <div class="cgt-mon-label">
            <span class="cgt-mon-label-title">ECG · II</span>
            <span class="cgt-mon-label-sub" data-ecg-note>25 mm/s · 10 mm/mV</span>
          </div>
          <canvas data-ecg></canvas>
        </section>

        <section class="cgt-mon-value cgt-mon-hr" data-vital="hr">
          <div class="cgt-mon-value-head">
            <span class="cgt-mon-v-label">HR</span>
            <span class="cgt-mon-v-unit">bpm</span>
          </div>
          <div class="cgt-mon-v-num" data-v-num>--</div>
          <div class="cgt-mon-v-ref" data-v-ref>— — —</div>
        </section>

        <section class="cgt-mon-waveform cgt-mon-pleth">
          <div class="cgt-mon-label">
            <span class="cgt-mon-label-title">SpO₂ · PLETH</span>
          </div>
          <canvas data-pleth></canvas>
        </section>

        <section class="cgt-mon-value cgt-mon-spo2" data-vital="spo2">
          <div class="cgt-mon-value-head">
            <span class="cgt-mon-v-label">SpO₂</span>
            <span class="cgt-mon-v-unit">%</span>
          </div>
          <div class="cgt-mon-v-num" data-v-num>--</div>
          <div class="cgt-mon-v-ref" data-v-ref>≥ 95</div>
        </section>

        <section class="cgt-mon-value cgt-mon-nibp" data-vital="nibp">
          <div class="cgt-mon-value-head">
            <span class="cgt-mon-v-label">NIBP</span>
            <span class="cgt-mon-v-unit">mmHg</span>
          </div>
          <div class="cgt-mon-v-num" data-v-num>---/--</div>
          <div class="cgt-mon-v-ref" data-v-ref>MAP --</div>
        </section>

        <section class="cgt-mon-value cgt-mon-rr" data-vital="rr">
          <div class="cgt-mon-value-head">
            <span class="cgt-mon-v-label">RR</span>
            <span class="cgt-mon-v-unit">rpm</span>
          </div>
          <div class="cgt-mon-v-num" data-v-num>--</div>
          <div class="cgt-mon-v-ref" data-v-ref>12-20</div>
        </section>

        <section class="cgt-mon-value cgt-mon-temp" data-vital="temp">
          <div class="cgt-mon-value-head">
            <span class="cgt-mon-v-label">TEMP</span>
            <span class="cgt-mon-v-unit">°C</span>
          </div>
          <div class="cgt-mon-v-num" data-v-num>--</div>
          <div class="cgt-mon-v-ref" data-v-ref>36-37.5</div>
        </section>
      </div>
      <aside class="cgt-mon-sidebar">
        <div class="cgt-mon-actions">
          <h3>Acciones clínicas</h3>
          <div class="cgt-mon-actions-grid">
            ${CLINICAL_ACTIONS.map(a => `
              <button type="button" data-action="${a.id}" data-action-label="${this.esc(a.label)}"
                ${a.detail ? `data-action-detail="${this.esc(a.detail)}"` : ''}>
                <span>${a.icon}</span> ${this.esc(a.label)}
              </button>
            `).join('')}
          </div>
        </div>
        <div class="cgt-mon-drugs">
          <h3>Farmacoterapia</h3>
          <div class="cgt-mon-drugs-grid">
            ${DRUG_CATALOG.map(d => `
              <button type="button" data-drug="${d.id}" title="${this.esc(d.note ?? d.name)}">
                <span>${d.icon ?? '💉'}</span>
                <span class="cgt-mon-drug-name">${this.esc(d.name)}</span>
                <span class="cgt-mon-drug-route">${d.route}</span>
              </button>
            `).join('')}
          </div>
        </div>
        <div class="cgt-mon-log" data-log>
          <h3>Bitácora del box</h3>
          <ul data-log-list>
            <li class="cgt-mon-log-system">Sistema inicializado. Monitorizando al paciente.</li>
          </ul>
        </div>
      </aside>
      <footer class="cgt-mon-alarmbar" data-alarmbar>
        <span data-alarmtext>Sistema listo</span>
      </footer>
    `;
  }

  private resizeCanvases(): void {
    if (this.ecgCanvas) {
      this.ecgCanvas.width = this.ecgCanvas.clientWidth * devicePixelRatio;
      this.ecgCanvas.height = this.ecgCanvas.clientHeight * devicePixelRatio;
    }
    if (this.plethCanvas) {
      this.plethCanvas.width = this.plethCanvas.clientWidth * devicePixelRatio;
      this.plethCanvas.height = this.plethCanvas.clientHeight * devicePixelRatio;
    }
  }

  private loop(): void {
    const now = performance.now();
    const dt = Math.min(0.05, (now - this.lastFrame) / 1000);
    this.lastFrame = now;

    // Leer vitales del paciente para actualizar valores numéricos + waveforms
    const vitals = this.readVitals();
    this.updateVitalBoxes(vitals);
    this.syncGenerators(vitals);
    this.updateClock();

    // Waveform ECG: 1 muestra por frame
    this.ecgBuffer.push(this.ecgGen.sample(dt));
    if (this.ecgBuffer.length > this.SAMPLES) this.ecgBuffer.shift();
    this.drawECG();

    // Waveform SpO₂
    this.plethBuffer.push(this.plethGen.sample(dt));
    if (this.plethBuffer.length > this.SAMPLES) this.plethBuffer.shift();
    this.drawPleth();

    // Alarma global
    this.updateAlarm(vitals);

    // HUD de detección de gestos clínicos: actualiza barras de progreso
    // de RCP/defib/pulse cada frame (cheap, solo lee floats).
    this.updateGestureHud();

    this.rafId = requestAnimationFrame(() => this.loop());
  }

  // ─────────────────────────────────────────────────────────
  // Lectura de vitales del Patient → VitalRead estándar
  // ─────────────────────────────────────────────────────────

  private readVitals(): Record<string, VitalRead> {
    const out: Record<string, VitalRead> = {};
    // Defaults para valores ausentes
    const defaults: Record<string, { display: string; unit: string; color: string; refLow?: number; refHigh?: number }> = {
      hr: { display: 'HR', unit: 'bpm', color: '#30D158', refLow: 60, refHigh: 100 },
      spo2: { display: 'SpO₂', unit: '%', color: '#2997FF', refLow: 95, refHigh: 100 },
      sbp: { display: 'PAS', unit: 'mmHg', color: '#FF453A', refLow: 90, refHigh: 140 },
      dbp: { display: 'PAD', unit: 'mmHg', color: '#FF453A', refLow: 60, refHigh: 90 },
      rr: { display: 'RR', unit: 'rpm', color: '#FFD60A', refLow: 12, refHigh: 20 },
      temp: { display: 'TEMP', unit: '°C', color: '#F5F5F7', refLow: 36, refHigh: 37.5 },
    };

    if (!this.patient) {
      for (const k of Object.keys(defaults)) {
        out[k] = {
          code: k, display: defaults[k].display, value: null, unit: defaults[k].unit,
          color: defaults[k].color, level: 'off',
          refLow: defaults[k].refLow, refHigh: defaults[k].refHigh,
        };
      }
      return out;
    }

    // Mapear LOINC codes conocidos a keys estándar
    const codeMap: Record<string, string> = {
      '8867-4': 'hr',
      '8480-6': 'sbp',
      '8462-4': 'dbp',
      '59408-5': 'spo2', '2708-6': 'spo2',
      '9279-1': 'rr',
      '8310-5': 'temp',
    };

    const found: Record<string, number | null> = {};
    this.patient.data.nodes.forEach((n: ClinicalNodeData) => {
      const k = codeMap[n.concept.code];
      if (k && n.valueQuantity !== undefined) {
        found[k] = n.valueQuantity.value;
      }
    });

    // Sprint 4: deltas farmacodinámicos por drogas administradas (curvas
    // rise/plateau/decay en tiempo real). Se suman a los overrides fijos
    // que pueda devolver la IA desde /ai/clinical-action.
    const drugDeltas = physiologyEngine.getDeltas();

    for (const k of Object.keys(defaults)) {
      let v = found[k] ?? null;
      // Aplicar override acumulado de la IA (respuestas a acciones clínicas).
      // Ej: dio O₂ → spo2 delta +4 → v + 4.
      if (v !== null && this.vitalsOverride[k] !== undefined) {
        v += this.vitalsOverride[k];
      }
      const pharmaDelta = drugDeltas[k as keyof typeof drugDeltas];
      if (v !== null && pharmaDelta !== undefined) {
        v += pharmaDelta;
      }
      const d = defaults[k];
      out[k] = {
        code: k, display: d.display, value: v, unit: d.unit, color: d.color,
        refLow: d.refLow, refHigh: d.refHigh,
        level: this.classifyAlarm(v, d.refLow, d.refHigh, k),
      };
    }
    return out;
  }

  /**
   * Clasifica nivel de alarma. Extensiones específicas por vital
   * (ej. SpO₂ < 90 es crítica directamente, FC < 40 es crítica, etc.)
   */
  private classifyAlarm(v: number | null, low?: number, high?: number, kind?: string): AlarmLevel {
    if (v === null) return 'off';
    // Umbrales críticos por vital
    if (kind === 'spo2' && v < 90) return 'critical';
    if (kind === 'hr' && (v < 40 || v > 140)) return 'critical';
    if (kind === 'sbp' && (v < 80 || v > 200)) return 'critical';
    if (kind === 'rr' && (v < 8 || v > 30)) return 'critical';
    if (kind === 'temp' && (v < 34 || v > 39.5)) return 'critical';
    // Warning: fuera de rango pero no crítico
    if (low !== undefined && v < low) return 'warning';
    if (high !== undefined && v > high) return 'warning';
    return 'off';
  }

  private updateVitalBoxes(vitals: Record<string, VitalRead>): void {
    if (!this.root) return;
    const fmt = (v: number | null, digits = 0): string =>
      v === null ? '--' : v.toFixed(digits);

    this.setVitalBox('hr', fmt(vitals.hr.value), vitals.hr, '60-100 bpm');
    this.setVitalBox('spo2', fmt(vitals.spo2.value), vitals.spo2, '≥ 95 %');

    const sbp = vitals.sbp.value;
    const dbp = vitals.dbp.value;
    const map = (sbp !== null && dbp !== null) ? Math.round((sbp + 2 * dbp) / 3) : null;
    const nibpStr = (sbp !== null && dbp !== null) ? `${fmt(sbp)}/${fmt(dbp)}` : '---/--';
    const level: AlarmLevel = vitals.sbp.level === 'critical' || vitals.dbp.level === 'critical'
      ? 'critical'
      : (vitals.sbp.level === 'warning' || vitals.dbp.level === 'warning' ? 'warning' : 'off');
    this.setVitalBox('nibp', nibpStr, { ...vitals.sbp, level }, `MAP ${map ?? '--'}`);

    this.setVitalBox('rr', fmt(vitals.rr.value), vitals.rr, '12-20 rpm');
    this.setVitalBox('temp', fmt(vitals.temp.value, 1), vitals.temp, '36-37.5 °C');

    // Ficha tipo hospitalaria: nombre + id + edad/sexo + alergias
    const name = this.root.querySelector<HTMLElement>('[data-pname]');
    const demo = this.root.querySelector<HTMLElement>('[data-pdemo]');
    const pid = this.root.querySelector<HTMLElement>('[data-pid]');
    const alerg = this.root.querySelector<HTMLElement>('[data-palergias]');
    if (this.patient && name && demo && pid) {
      const p = this.patient.data;
      const d = p.demographics;
      // Nombre del paciente: si existe en el grafo lo usamos; si no un
      // genérico con el ID (muchos casos no tienen name explícito).
      // Nombres ficticios determinísticos por case_id para que el alumno
      // viva el caso como "Don Juan" y no como "Paciente masculino" (más
      // inmersivo, estilo OSCE). No son datos reales — son plantilla.
      const rawName = (p as unknown as { patientName?: string }).patientName
        ?? generateFictionalName(p.id, d.sex);
      name.textContent = rawName;
      demo.textContent = `${d.age}a · ${d.sex === 'male' ? 'M' : d.sex === 'female' ? 'F' : 'O'}${d.weight ? ` · ${d.weight}kg` : ''}${d.height ? ` · ${d.height}cm` : ''}`;
      pid.textContent = p.id;
      // Alergias: buscar nodo de tipo allergy; si no, "Sin alergias conocidas"
      if (alerg) {
        const allergyNode = p.nodes.find(n => n.type === 'allergy');
        if (allergyNode) {
          alerg.innerHTML = `⚠️ ALERGIA: ${this.esc(allergyNode.concept.display)}`;
          alerg.classList.add('cgt-mon-has-allergy');
        } else {
          alerg.textContent = 'Sin alergias conocidas';
          alerg.classList.remove('cgt-mon-has-allergy');
        }
      }
    }
  }

  private esc(text: string): string {
    return text.replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c] ?? c));
  }

  // ─────────────────────────────────────────────────────────
  // Gestos clínicos: la cámara detecta y "activa cosas" (feedback de Leo)
  // ─────────────────────────────────────────────────────────

  /**
   * Tiempo en ms que la palma debe mantenerse abierta y quieta para que
   * dispare auscultación. Es el gesto clínico natural — el residente
   * apoya la palma sobre la zona a auscultar.
   */
  private readonly AUSCULT_HOLD_MS = 1500;
  private palmHoldStart = 0;
  private palmLocked = false;
  private handGestureRaf: number | null = null;
  private lastAuscultationAt = 0;

  // IA protagónica: historial de acciones + timer de narrativa automática
  private actionHistory: string[] = [];
  /** Override de vitales aplicado por las respuestas de IA (delta acumulado). */
  private vitalsOverride: Record<string, number> = {};
  private narrativeTimer: number | null = null;
  private startedAt = 0;

  private startHandGestureLoop(): void {
    const tick = (): void => {
      if (!this.root) return;
      this.evaluateHandGesture();
      this.handGestureRaf = requestAnimationFrame(tick);
    };
    tick();
  }

  /**
   * Evalúa el estado actual de las manos (expuesto por HandController en
   * `state.leftHand` / `state.rightHand`). Si una palma está abierta, quieta
   * y sostenida >1.5s, dispara auscultación. Lock post-fire evita loops.
   */
  private evaluateHandGesture(): void {
    const hand = state.rightHand.isPresent ? state.rightHand : state.leftHand.isPresent ? state.leftHand : null;
    const now = Date.now();
    if (!hand || !hand.landmarks) {
      this.palmHoldStart = 0;
      this.palmLocked = false;
      this.updatePalmOverlay(0);
      return;
    }
    // Openness: usamos la detección existente. Si hay landmarks, medimos
    // distancia wrist→tips. Palma abierta: distancia promedio ≥ cierto umbral.
    const openness = this.estimateOpenness(hand.landmarks);
    const velocity = hand.velocity.length();
    const isOpenPalm = openness > 0.7 && velocity < 0.05;

    if (isOpenPalm && !this.palmLocked) {
      if (this.palmHoldStart === 0) this.palmHoldStart = now;
      const heldFor = now - this.palmHoldStart;
      this.updatePalmOverlay(Math.min(1, heldFor / this.AUSCULT_HOLD_MS));
      if (heldFor >= this.AUSCULT_HOLD_MS) {
        this.palmLocked = true;
        this.palmHoldStart = 0;
        this.updatePalmOverlay(0);
        this.triggerAuscultation('gesture');
      }
    } else if (!isOpenPalm) {
      this.palmHoldStart = 0;
      this.palmLocked = false;
      this.updatePalmOverlay(0);
    }
  }

  private estimateOpenness(landmarks: Array<{ x: number; y: number; z: number }>): number {
    if (!landmarks || landmarks.length < 21) return 0;
    // Distancia promedio wrist → tips de los 5 dedos
    const wrist = landmarks[0];
    const tipIdxs = [4, 8, 12, 16, 20];
    let total = 0;
    for (const i of tipIdxs) {
      const t = landmarks[i];
      if (!t) continue;
      total += Math.hypot(t.x - wrist.x, t.y - wrist.y, t.z - wrist.z);
    }
    const avg = total / tipIdxs.length;
    // Normalizar (empírico, 0.35 = palma bien abierta, 0.15 = puño)
    return Math.max(0, Math.min(1, (avg - 0.15) / 0.20));
  }

  private updatePalmOverlay(progress: number): void {
    let el = this.root?.querySelector<HTMLElement>('[data-palm-overlay]');
    if (progress <= 0) {
      if (el) el.style.opacity = '0';
      return;
    }
    if (!el) return;
    el.style.opacity = '1';
    const fill = el.querySelector<HTMLElement>('.cgt-mon-palm-fill');
    if (fill) fill.style.width = `${Math.round(progress * 100)}%`;
  }

  /**
   * Dispara la auscultación: revela un finding auscultatorio del paciente
   * y lo muestra como sticker flotante sobre el monitor.
   *
   * Si se invoca sin zona específica, abre el mapa corporal para que el
   * alumno seleccione dónde auscultar. Si viene con zona, reproduce el
   * hallazgo específico de esa zona (con TTS opcional).
   */
  private triggerAuscultation(source: 'click' | 'gesture', zone?: AuscZone): void {
    // Debounce: no re-disparar en los siguientes 3s
    const now = Date.now();
    if (now - this.lastAuscultationAt < 3000) return;
    this.lastAuscultationAt = now;

    // Sin zona → abrir mapa corporal
    if (!zone) {
      this.showAuscultationMap();
      return;
    }

    const zoneDef = AUSC_ZONES.find(z => z.id === zone);
    if (!zoneDef) return;
    const dx = this.patient?.data.nodes.find(n => n.type === 'diagnosis')?.concept.display;
    const finding = localFindingFor(zone, dx);
    this.showAuscultationOverlay(`${zoneDef.label}: ${finding}`, source);
    this.speakFinding(finding);
    this.appendLog('ai', `👂 Auscultación en ${zoneDef.short}: ${finding}`);
  }

  /** Abre overlay con el mapa corporal y zonas clickeables. */
  private showAuscultationMap(): void {
    if (!this.root) return;
    let map = this.root.querySelector<HTMLElement>('.cgt-mon-auscmap');
    if (map) { map.remove(); }
    map = document.createElement('div');
    map.className = 'cgt-mon-auscmap';
    map.innerHTML = `
      <div class="cgt-mon-auscmap-card">
        <header>
          <span>👂 Seleccioná zona de auscultación</span>
          <button type="button" data-ausc-close aria-label="Cerrar">×</button>
        </header>
        <div class="cgt-mon-auscmap-body">
          <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" class="cgt-mon-auscmap-svg">
            <!-- Silueta simplificada del tórax (cabeza + torso + brazos abstractos) -->
            <g fill="none" stroke="rgba(255,255,255,0.35)" stroke-width="0.6">
              <!-- Cabeza -->
              <ellipse cx="50" cy="15" rx="7" ry="8"/>
              <!-- Cuello -->
              <line x1="47" y1="22" x2="47" y2="26"/>
              <line x1="53" y1="22" x2="53" y2="26"/>
              <!-- Tórax -->
              <path d="M 32 28 Q 30 55 34 68 L 66 68 Q 70 55 68 28 Z" fill="rgba(255,255,255,0.03)"/>
              <!-- Línea media esternal -->
              <line x1="50" y1="28" x2="50" y2="60" stroke-dasharray="1,1"/>
              <!-- Brazos abstractos -->
              <line x1="32" y1="30" x2="22" y2="58"/>
              <line x1="68" y1="30" x2="78" y2="58"/>
            </g>
            ${AUSC_ZONES.map(z => `
              <g class="cgt-mon-auscmap-zone cgt-mon-auscmap-zone-${z.category}" data-ausc-zone="${z.id}">
                <circle cx="${z.x}" cy="${z.y}" r="4" />
                <text x="${z.x}" y="${z.y + 1.2}" text-anchor="middle" font-size="2.8" fill="#fff">${z.short}</text>
                <title>${z.label}</title>
              </g>
            `).join('')}
          </svg>
          <div class="cgt-mon-auscmap-hint">
            Hacé click en un foco cardíaco (Ao/Pu/Tr/Mi) o campo pulmonar (VPD/VPI/BPD/BPI).
            El paciente no se mueve — como un monitor real, solo ves lo que auscultás.
          </div>
        </div>
      </div>
    `;
    this.root.appendChild(map);

    map.querySelector<HTMLButtonElement>('[data-ausc-close]')?.addEventListener('click', () => map?.remove());
    map.querySelectorAll<SVGElement>('[data-ausc-zone]').forEach(el => {
      el.addEventListener('click', () => {
        const z = (el as unknown as SVGElement).getAttribute('data-ausc-zone') as AuscZone;
        map?.remove();
        // Reset debounce para no bloquear la zona seleccionada
        this.lastAuscultationAt = 0;
        this.triggerAuscultation('click', z);
      });
    });
  }

  /** Verbaliza el hallazgo con TTS para inmersión (voz "del residente"). */
  private speakFinding(text: string): void {
    if (!window.speechSynthesis) return;
    try {
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = 'es-CL';
      utter.rate = 1;
      utter.pitch = 1;
      utter.volume = 0.85;
      window.speechSynthesis.speak(utter);
    } catch { /* ignore */ }
  }

  private pickAuscultationFinding(): string {
    if (!this.patient) return 'Sin hallazgos auscultatorios relevantes.';
    const nodes = this.patient.data.nodes;

    // Primero buscar un nodo imaging o symptom con keyword auscultación
    const kw = /(soplo|estert|sibilan|crepit|ausculta|r1|r2|r3|roce|murmur)/i;
    const match = nodes.find(n => kw.test(n.concept.display) || (n.valueString && kw.test(n.valueString)));
    if (match) {
      const value = match.valueString ?? match.concept.display;
      return `${match.concept.display}: ${value}`;
    }

    // Inferir desde el diagnóstico
    const dx = nodes.find(n => n.type === 'diagnosis')?.concept.display ?? '';
    if (/STEMI|infarto/i.test(dx)) {
      return 'R1/R2 presentes, sin soplos. Ritmo regular. Sin 3er ruido.';
    }
    if (/sepsis|shock/i.test(dx)) {
      return 'Taquicardia regular, sin soplos. R1/R2 apagados.';
    }
    if (/EPOC|asma|respirat/i.test(dx)) {
      return 'Murmullo pulmonar disminuido bilateral, sibilancias espiratorias difusas.';
    }
    if (/neumon/i.test(dx)) {
      return 'Crepitantes finos en base pulmonar. Soplo tubárico localizado.';
    }
    return 'Auscultación sin hallazgos patológicos evidentes.';
  }

  // ─────────────────────────────────────────────────────────
  // IA protagónica: acciones clínicas con respuesta de Kimi
  // ─────────────────────────────────────────────────────────

  /**
   * El alumno clickea o gesticula una acción clínica (auscultar, palpar,
   * administrar oxígeno, etc). La IA responde con el efecto realista:
   * narrativa breve + delta de vitales + posible reclasificación de triaje.
   *
   * Mientras la IA procesa (5-30s), se muestra un "Procesando…" en el log.
   */
  private async performClinicalAction(id: string, label: string, detail: string | undefined): Promise<void> {
    this.appendLog('action', `Acción: ${label}${detail ? ` (${detail})` : ''}`);
    this.actionHistory.push(label);
    sessionRecorder.record(
      id.startsWith('drug:') ? 'drug' : 'action',
      `${label}${detail ? ` (${detail})` : ''}`,
      { id, detail },
    );
    if (id.startsWith('drug:')) {
      liveSession.send({ type: 'student.drug', name: label, dose: detail ?? '' });
    } else {
      liveSession.send({ type: 'student.action', label, detail });
    }

    if (id === 'auscultar-torax' || id === 'auscultar-abdomen') {
      // La auscultación tiene su propio overlay visual además del log
      this.triggerAuscultation('click');
    }

    if (!authService.isAuthenticated || !this.patient) {
      this.appendLog('system', 'IA no disponible sin sesión — acción registrada.');
      return;
    }

    const processingId = this.appendLog('pending', `⏳ IA procesando respuesta clínica…`);

    try {
      const vitals = this.readVitals();
      const currentVitalsDict: Record<string, number | null> = {
        hr: vitals.hr.value, sbp: vitals.sbp.value, dbp: vitals.dbp.value,
        spo2: vitals.spo2.value, rr: vitals.rr.value, temp: vitals.temp.value,
      };
      const dxNode = this.patient.data.nodes.find(n => n.type === 'diagnosis');
      const res = await authService.request<ClinicalActionResponse>('/ai/clinical-action', {
        method: 'POST',
        body: JSON.stringify({
          case_id: this.patient.data.id,
          chief_complaint: this.patient.data.chiefComplaint ?? '',
          diagnosis: dxNode?.concept.display,
          current_vitals: currentVitalsDict,
          action: label,
          action_detail: detail,
          history: this.actionHistory,
        }),
      });
      // Reemplazar mensaje de "procesando" con la respuesta
      this.removeLogItem(processingId);
      this.appendLog('ai', `🧠 ${res.narrative}`);
      if (res.teaching_note) {
        this.appendLog('teach', `💡 ${res.teaching_note}`);
      }
      // Aplicar delta de vitales (se suman al override). EXCEPTO para drogas:
      // el PhysiologyEngine ya aplica la curva rise/plateau/decay y sumar el
      // delta de la IA acá duplicaría el efecto (droga subiría el doble).
      if (res.vitals_delta && !id.startsWith('drug:')) {
        for (const k of Object.keys(res.vitals_delta)) {
          this.vitalsOverride[k] = (this.vitalsOverride[k] ?? 0) + res.vitals_delta[k];
        }
      }
    } catch (e) {
      this.removeLogItem(processingId);
      this.appendLog('error', `⚠️ IA sin respuesta: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  /**
   * Sprint 4: administra una droga del catálogo. La farmacología responde
   * con rampa/plateau/decay (PhysiologyEngine) y además pedimos a la IA
   * una narrativa breve para reforzar el rationale clínico.
   */
  private async administerDrug(drugId: string): Promise<void> {
    const drug = DRUG_CATALOG.find(d => d.id === drugId);
    if (!drug) return;
    physiologyEngine.administer(drugId);
    this.appendLog('action', `${drug.icon ?? '💉'} ${drug.name} administrada (${drug.route})`);
    // Reutilizar performClinicalAction para que Kimi genere narrativa.
    // Marcamos el id con prefijo 'drug:' para que el backend/log lo detecte.
    void this.performClinicalAction(`drug:${drug.id}`, drug.name, drug.dose);
    // Snapshots extra para que el profesor vea el efecto: inmediato (baseline)
    // y a +30s (cerca del peak típico). Ambos sin reemplazar el interval base.
    this.broadcastVitalsSnapshot();
    window.setTimeout(() => this.broadcastVitalsSnapshot(), 30000);
  }

  /**
   * Sprint 3: gestos de procedimiento detectados por MediaPipe disparan
   * las mismas acciones clínicas que los botones — con la diferencia de
   * que acá el alumno las ejecuta con las manos como en la vida real.
   *
   * El feedback del doc fue "tu fuerte es la cámara detectando manos y
   * activando cosas"; esto es exactamente esa promesa para RCP/defib/pulse.
   */
  private onClinicalGesture(ev: ClinicalGestureEvent): void {
    // Feedback visual inmediato: flash en el monitor
    this.flashMonitor(ev.id === 'defib-shock' ? 'critical' : 'info');
    sessionRecorder.record('gesture', ev.id, ev.data);
    liveSession.send({ type: 'student.gesture', gesture: ev.id, data: ev.data });

    switch (ev.id) {
      case 'chest-compressions': {
        const rate = Number(ev.data?.rate ?? 0);
        const count = Number(ev.data?.count ?? 0);
        const label = `RCP · compresiones torácicas`;
        const detail = `${count} comp, ${rate}/min`;
        this.appendLog('action', `🫸 Gesto detectado: RCP ${rate}/min (${count} comp)`);
        if (rate > 0 && (rate < 100 || rate > 120)) {
          this.appendLog('teach', `💡 Rango ideal: 100-120 comp/min (detectado ${rate}).`);
        }
        void this.performClinicalAction('rcp-gesto', label, detail);
        break;
      }
      case 'defib-shock': {
        this.appendLog('action', `⚡ Gesto detectado: Desfibrilación`);
        void this.performClinicalAction('defib-gesto', 'Desfibrilación', 'Shock 200J bifásico');
        break;
      }
      case 'pulse-check': {
        this.appendLog('action', `🫱 Gesto detectado: Verificación de pulso`);
        void this.performClinicalAction('pulso-gesto', 'Verificar pulso carotídeo', undefined);
        break;
      }
    }
  }

  /** Flash rápido en el borde del monitor para confirmar un gesto. */
  private flashMonitor(level: 'info' | 'critical'): void {
    if (!this.root) return;
    const cls = level === 'critical' ? 'cgt-mon-flash-crit' : 'cgt-mon-flash-info';
    this.root.classList.add(cls);
    window.setTimeout(() => this.root?.classList.remove(cls), 500);
  }

  /**
   * Narrativa automática cada 25-40s: la IA genera un evento contextual
   * (paciente habla, enfermería reporta, llega familiar). Ocurre sin que
   * el alumno haga nada — replica la realidad de un box clínico.
   */
  private scheduleNarrativeTick(): void {
    // Jitter entre 25s y 40s para que no sea predecible
    const delay = 25000 + Math.random() * 15000;
    this.narrativeTimer = window.setTimeout(() => {
      void this.fetchNarrativeTick();
    }, delay);
  }

  private async fetchNarrativeTick(): Promise<void> {
    if (!this.root || !this.patient || !authService.isAuthenticated) {
      this.scheduleNarrativeTick();
      return;
    }
    try {
      const vitals = this.readVitals();
      const dxNode = this.patient.data.nodes.find(n => n.type === 'diagnosis');
      const res = await authService.request<NarrativeEventResponse>('/ai/clinical-narrate', {
        method: 'POST',
        body: JSON.stringify({
          case_id: this.patient.data.id,
          chief_complaint: this.patient.data.chiefComplaint ?? '',
          diagnosis: dxNode?.concept.display,
          current_vitals: {
            hr: vitals.hr.value, sbp: vitals.sbp.value, spo2: vitals.spo2.value,
            rr: vitals.rr.value, temp: vitals.temp.value,
          },
          elapsed_sec: Math.round((Date.now() - this.startedAt) / 1000),
          history: this.actionHistory,
        }),
      });
      if (res.kind !== 'none' && res.message) {
        const icon = res.kind === 'patient' ? '🗣️'
          : res.kind === 'nurse' ? '🩺'
          : res.kind === 'family' ? '👨‍👩‍👧'
          : '💬';
        const source = res.kind === 'patient' ? 'Paciente'
          : res.kind === 'nurse' ? 'Enfermería'
          : res.kind === 'family' ? 'Familiar'
          : 'Sistema';
        this.appendLog(res.urgency === 'critical' ? 'error' : 'ai',
          `${icon} ${source}: "${res.message}"`);
      }
    } catch (e) {
      // fail-silent; simplemente re-agendamos
      console.warn('[monitor] narrativa IA falló:', e);
    }
    this.scheduleNarrativeTick();
  }

  // ─────────────────────────────────────────────────────────
  // Bitácora del box (log clínico)
  // ─────────────────────────────────────────────────────────

  private logCounter = 0;

  private appendLog(kind: 'system' | 'action' | 'ai' | 'teach' | 'error' | 'pending', message: string): string {
    const list = this.root?.querySelector<HTMLElement>('[data-log-list]');
    if (!list) return '';
    const id = `log-${++this.logCounter}`;
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    const li = document.createElement('li');
    li.className = `cgt-mon-log-${kind}`;
    li.id = id;
    li.innerHTML = `<time>${hh}:${mm}:${ss}</time><span>${this.esc(message)}</span>`;
    list.appendChild(li);
    list.scrollTop = list.scrollHeight;
    return id;
  }

  private removeLogItem(id: string): void {
    if (!id) return;
    this.root?.querySelector(`#${id}`)?.remove();
  }

  private showAuscultationOverlay(finding: string, source: 'click' | 'gesture'): void {
    if (!this.root) return;
    let panel = this.root.querySelector<HTMLElement>('.cgt-mon-auscult');
    if (!panel) {
      panel = document.createElement('div');
      panel.className = 'cgt-mon-auscult';
      this.root.appendChild(panel);
    }
    panel.innerHTML = `
      <div class="cgt-mon-auscult-card">
        <div class="cgt-mon-auscult-head">
          <span>👂 Auscultación</span>
          ${source === 'gesture' ? '<small>Gesto detectado</small>' : ''}
        </div>
        <p>${this.esc(finding)}</p>
      </div>
    `;
    panel.classList.add('cgt-mon-auscult-visible');
    // Bip corto de confirmación via AlarmSound (reutilizo su audioContext)
    alarmSound.prime();
    // No tengo acceso directo al tone privado, asi que disparamos una alarma
    // warning corta de un frame y la apagamos (no ideal pero funciona).
    // En iteración futura: exponer API pública de tone en AlarmSound.
    window.setTimeout(() => panel?.classList.remove('cgt-mon-auscult-visible'), 5500);
  }

  private setVitalBox(
    key: string,
    display: string,
    vital: VitalRead,
    refStr: string,
  ): void {
    const box = this.root?.querySelector<HTMLElement>(`[data-vital="${key}"]`);
    if (!box) return;
    const numEl = box.querySelector<HTMLElement>('[data-v-num]');
    const refEl = box.querySelector<HTMLElement>('[data-v-ref]');
    if (numEl) {
      numEl.textContent = display;
      numEl.style.color = vital.color;
    }
    if (refEl) refEl.textContent = refStr;
    box.classList.toggle('cgt-mon-alarm-crit', vital.level === 'critical');
    box.classList.toggle('cgt-mon-alarm-warn', vital.level === 'warning');
  }

  private syncGenerators(vitals: Record<string, VitalRead>): void {
    const hr = vitals.hr.value ?? 72;
    this.ecgGen.setHeartRate(hr);
    this.plethGen.setHeartRate(hr);

    // Detectar ritmo desde el caso. v1: STEMI si hay troponina elevada o si
    // el dx tiene "STEMI"/"infarto" en el display. Asistole si HR=0.
    // Mejora futura: el profesor elige el ritmo al crear el caso.
    if (!this.patient) return;
    const nodes = this.patient.data.nodes;
    const hasStemi = nodes.some(n =>
      n.type === 'diagnosis' && /STEMI|infarto.*elevaci/i.test(n.concept.display)
    );
    const hasHighTroponin = nodes.some(n => {
      if (n.concept.code === '6598-7' || n.concept.code === '10839-9') {
        const ref = n.referenceRange?.high ?? 0;
        return (n.valueQuantity?.value ?? 0) > ref * 3;
      }
      return false;
    });
    const rhythm: ECGRhythm = hr === 0 ? 'asistole'
      : (hasStemi || hasHighTroponin) ? 'stemi'
      : hr > 120 ? 'tachy'
      : 'sinus';
    this.ecgGen.setRhythm(rhythm);
  }

  private updateClock(): void {
    const el = this.root?.querySelector<HTMLElement>('[data-clock]');
    const dateEl = this.root?.querySelector<HTMLElement>('[data-clockdate]');
    if (!el) return;
    const now = new Date();
    el.textContent = now.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    if (dateEl) {
      dateEl.textContent = now.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', weekday: 'short' });
    }
  }

  // ─────────────────────────────────────────────────────────
  // Drawing
  // ─────────────────────────────────────────────────────────

  private drawECG(): void {
    const c = this.ecgCanvas;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const w = c.width, h = c.height;
    // Fondo negro + grid
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);
    this.drawGrid(ctx, w, h, '#1a3020');

    // Trazado: escalar mV a pixels (1 mV = 1/5 de alto)
    ctx.strokeStyle = '#30D158';
    ctx.lineWidth = 2 * devicePixelRatio;
    ctx.beginPath();
    const n = this.ecgBuffer.length;
    const stepX = w / this.SAMPLES;
    const midY = h * 0.55;
    const scaleY = h * 0.28; // 1 mV ≈ 28% del alto
    for (let i = 0; i < n; i++) {
      const x = i * stepX;
      const y = midY - this.ecgBuffer[i] * scaleY;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }

  private drawPleth(): void {
    const c = this.plethCanvas;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const w = c.width, h = c.height;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);
    this.drawGrid(ctx, w, h, '#0a2340');

    ctx.strokeStyle = '#2997FF';
    ctx.lineWidth = 2 * devicePixelRatio;
    ctx.beginPath();
    const n = this.plethBuffer.length;
    const stepX = w / this.SAMPLES;
    const baseY = h * 0.85;
    const scaleY = h * 0.65;
    for (let i = 0; i < n; i++) {
      const x = i * stepX;
      const y = baseY - this.plethBuffer[i] * scaleY;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }

  private drawGrid(ctx: CanvasRenderingContext2D, w: number, h: number, color: string): void {
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    const step = 25 * devicePixelRatio;
    ctx.beginPath();
    for (let x = 0; x < w; x += step) { ctx.moveTo(x, 0); ctx.lineTo(x, h); }
    for (let y = 0; y < h; y += step) { ctx.moveTo(0, y); ctx.lineTo(w, y); }
    ctx.stroke();
  }

  // ─────────────────────────────────────────────────────────
  // Alarmas (global)
  // ─────────────────────────────────────────────────────────

  private updateAlarm(vitals: Record<string, VitalRead>): void {
    let level: AlarmLevel = 'off';
    let message = 'Sistema listo';
    const crits: string[] = [];
    const warns: string[] = [];
    for (const k of Object.keys(vitals)) {
      const v = vitals[k];
      if (v.level === 'critical' && v.value !== null) {
        crits.push(`${v.display} ${v.value.toFixed(0)} ${v.unit} CRÍTICO`);
      } else if (v.level === 'warning' && v.value !== null) {
        warns.push(`${v.display} ${v.value.toFixed(0)} ${v.unit} fuera de rango`);
      }
    }
    if (crits.length > 0) {
      level = 'critical';
      message = `*** ALARMA *** ${crits.join(' · ')}`;
    } else if (warns.length > 0) {
      level = 'warning';
      message = warns.join(' · ');
    }
    const bar = this.root?.querySelector<HTMLElement>('[data-alarmbar]');
    const txt = this.root?.querySelector<HTMLElement>('[data-alarmtext]');
    if (bar) {
      bar.classList.toggle('cgt-mon-bar-crit', level === 'critical');
      bar.classList.toggle('cgt-mon-bar-warn', level === 'warning');
    }
    if (txt) txt.textContent = message;
    alarmSound.setLevel(level);
  }

  private updateSilenceButton(muted: boolean): void {
    const btn = this.root?.querySelector<HTMLButtonElement>('[data-silence]');
    if (!btn) return;
    btn.textContent = muted ? '🔇 Silenciado 60s' : '🔕 Silenciar';
    btn.disabled = muted;
    if (muted) {
      window.setTimeout(() => {
        btn.textContent = '🔕 Silenciar';
        btn.disabled = false;
      }, 60_000);
    }
  }

  private injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      /* Aislamiento total: en Monitor Mode el alumno debe ver SOLO la UI
         hospitalaria. El feedback clínico del doc fue que el grafo 3D
         (órganos, pacientes, nodos coloridos) es lo que hace sentir
         "académico y no operativo". Aquí escondemos todo lo que no
         pertenece al monitor. */
      body.cgt-monitor-active .clinical-node-label,
      body.cgt-monitor-active #cgt-legend,
      body.cgt-monitor-active .cgt-hud,
      body.cgt-monitor-active .cgt-hud-notifications,
      body.cgt-monitor-active .cgt-hud-triage,
      body.cgt-monitor-active .cgt-hud-status,
      body.cgt-monitor-active .cgt-hud-debug,
      body.cgt-monitor-active .cgt-hud-loading,
      body.cgt-monitor-active #cgt-hand-status,
      body.cgt-monitor-active #cgt-sidebar-toggle,
      body.cgt-monitor-active .cgt-sidebar,
      body.cgt-monitor-active #cgt-clockhud,
      body.cgt-monitor-active .cgt-progress-hud,
      body.cgt-monitor-active #progress-hud,
      body.cgt-monitor-active #cgt-hand-overlay,
      body.cgt-monitor-active #cgt-hand-cursor,
      body.cgt-monitor-active .cgt-notification,
      body.cgt-monitor-active .cgt-notifications,
      body.cgt-monitor-active .eop-narrative,
      body.cgt-monitor-active #exam-order-panel,
      body.cgt-monitor-active .cgt-camera-container,
      body.cgt-monitor-active .cgt-camera-video-container {
        display: none !important;
      }
      body.cgt-monitor-active #app > canvas,
      body.cgt-monitor-active #cgt-hand-overlay,
      body.cgt-monitor-active #cgt-hand-cursor,
      body.cgt-monitor-active #cgt-camera-video,
      body.cgt-monitor-active canvas:not(#${CONTAINER_ID} canvas) {
        display: none !important;
      }
      /* El video de la cámara lo escondemos también por si el layout PiP lo
         muestra — el monitor debe ser pantalla limpia sin distracciones. */
      body.cgt-monitor-active video {
        display: none !important;
      }
      /* Topología solo tiene sentido en modo grafo; fuera de ahí la ocultamos.
         El sidebar sigue visible, pero su bloque TOPOLOGÍA no (confuso
         para alumnos de triage/monitor). */
      body:not(.cgt-mode-grafo) .cgt-sb-graph-only {
        display: none !important;
      }

      #${CONTAINER_ID} {
        position: fixed; inset: 0;
        background: #000;
        color: #f5f5f7;
        font-family: 'JetBrains Mono', 'SF Mono', Consolas, monospace;
        display: grid;
        grid-template-rows: auto 1fr auto;
        grid-template-columns: 1fr 320px;
        grid-template-areas:
          "header header"
          "grid   sidebar"
          "alarm  alarm";
        overflow: hidden;
        z-index: 12000;
      }
      #${CONTAINER_ID} > header { grid-area: header; }
      #${CONTAINER_ID} .cgt-mon-grid { grid-area: grid; }
      #${CONTAINER_ID} .cgt-mon-sidebar { grid-area: sidebar; }
      #${CONTAINER_ID} .cgt-mon-alarmbar { grid-area: alarm; }
      @media (max-width: 900px) {
        #${CONTAINER_ID} {
          grid-template-columns: 1fr;
          grid-template-rows: auto 1fr auto auto;
          grid-template-areas: "header" "grid" "sidebar" "alarm";
        }
        #${CONTAINER_ID} .cgt-mon-sidebar { max-height: 40vh; }
      }

      #${CONTAINER_ID} .cgt-mon-sidebar {
        display: flex; flex-direction: column;
        background: #0a0a0a;
        border-left: 1px solid rgba(255,255,255,0.08);
        overflow: hidden;
      }
      #${CONTAINER_ID} .cgt-mon-actions {
        padding: 12px 14px; border-bottom: 1px solid rgba(255,255,255,0.06);
        flex-shrink: 0;
      }
      #${CONTAINER_ID} .cgt-mon-actions h3,
      #${CONTAINER_ID} .cgt-mon-log h3 {
        font-size: 10px; letter-spacing: 1.5px; color: #6e6e73;
        text-transform: uppercase; font-weight: 700; margin-bottom: 8px;
      }
      #${CONTAINER_ID} .cgt-mon-actions-grid {
        display: grid; grid-template-columns: 1fr 1fr; gap: 4px;
      }
      #${CONTAINER_ID} .cgt-mon-actions-grid button {
        background: rgba(255,255,255,0.04);
        border: 0.5px solid rgba(255,255,255,0.08);
        color: #d1d1d6;
        border-radius: 6px; padding: 7px 8px;
        font-family: -apple-system, 'Inter', sans-serif;
        font-size: 10.5px; text-align: left; cursor: pointer;
        display: flex; align-items: center; gap: 5px;
        line-height: 1.2;
        transition: background 0.15s;
      }
      #${CONTAINER_ID} .cgt-mon-actions-grid button:hover {
        background: rgba(41,151,255,0.15);
        border-color: rgba(41,151,255,0.35);
        color: #fff;
      }
      #${CONTAINER_ID} .cgt-mon-actions-grid button span { font-size: 13px; }

      #${CONTAINER_ID} .cgt-mon-log {
        flex: 1; min-height: 0;
        padding: 12px 14px;
        display: flex; flex-direction: column;
      }
      #${CONTAINER_ID} .cgt-mon-log ul {
        list-style: none; padding: 0; margin: 0; flex: 1;
        overflow-y: auto; font-size: 11.5px;
        font-family: -apple-system, 'Inter', sans-serif;
      }
      #${CONTAINER_ID} .cgt-mon-log li {
        padding: 6px 0; border-bottom: 0.5px solid rgba(255,255,255,0.04);
        line-height: 1.45;
      }
      #${CONTAINER_ID} .cgt-mon-log time {
        display: inline-block; width: 60px; color: #6e6e73;
        font-family: 'JetBrains Mono', monospace; font-size: 10.5px;
        margin-right: 4px;
      }
      #${CONTAINER_ID} .cgt-mon-log span { color: #d1d1d6; }
      #${CONTAINER_ID} .cgt-mon-log-system span { color: #8e8e93; }
      #${CONTAINER_ID} .cgt-mon-log-action span { color: #2997FF; font-weight: 600; }
      #${CONTAINER_ID} .cgt-mon-log-ai span { color: #30D158; }
      #${CONTAINER_ID} .cgt-mon-log-teach span { color: #FFD60A; }
      #${CONTAINER_ID} .cgt-mon-log-error span { color: #FF6961; }
      #${CONTAINER_ID} .cgt-mon-log-pending span {
        color: #BF5AF2;
        animation: cgt-mon-pending 1s ease-in-out infinite;
      }
      @keyframes cgt-mon-pending {
        50% { opacity: 0.4; }
      }
      #${CONTAINER_ID} .cgt-mon-header {
        display: flex; align-items: center; gap: 16px;
        padding: 8px 16px; background: #0a0a0a;
        border-bottom: 1px solid rgba(255,255,255,0.12);
        flex-shrink: 0;
      }
      #${CONTAINER_ID} .cgt-mon-bed {
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        padding: 6px 14px; border-right: 1px solid rgba(255,255,255,0.15);
        min-width: 80px;
      }
      #${CONTAINER_ID} .cgt-mon-bed-label {
        font-size: 9px; letter-spacing: 2px; color: #6e6e73; font-weight: 700;
      }
      #${CONTAINER_ID} .cgt-mon-bed-num {
        font-size: 32px; color: #30D158; font-weight: 800;
        font-family: 'JetBrains Mono', monospace; line-height: 1;
      }
      #${CONTAINER_ID} .cgt-mon-patient {
        flex: 1; min-width: 0; padding: 2px 0;
      }
      #${CONTAINER_ID} .cgt-mon-patient strong {
        display: block; font-size: 16px; color: #f5f5f7; letter-spacing: 0.3px;
        font-family: 'SF Pro', -apple-system, sans-serif;
      }
      #${CONTAINER_ID} .cgt-mon-patient-meta {
        display: flex; flex-wrap: wrap; gap: 12px;
        font-size: 11px; color: #a1a1a6; margin-top: 2px;
      }
      #${CONTAINER_ID} .cgt-mon-patient-id {
        font-family: 'JetBrains Mono', monospace; color: #8e8e93;
      }
      #${CONTAINER_ID} .cgt-mon-patient-alergias {
        color: #30D158;
      }
      #${CONTAINER_ID} .cgt-mon-patient-alergias.cgt-mon-has-allergy {
        color: #FFD60A; font-weight: 700;
      }
      #${CONTAINER_ID} .cgt-mon-clock-col {
        text-align: right; padding-right: 6px;
      }
      #${CONTAINER_ID} .cgt-mon-clock {
        font-size: 22px; color: #30D158; font-weight: 700;
        letter-spacing: 1.5px; font-family: 'JetBrains Mono', monospace;
        line-height: 1;
      }
      #${CONTAINER_ID} .cgt-mon-clock-date {
        font-size: 10px; color: #6e6e73; margin-top: 3px;
        text-transform: uppercase; letter-spacing: 0.8px;
      }
      #${CONTAINER_ID} .cgt-mon-header-actions {
        display: flex; gap: 6px; align-items: center;
      }
      #${CONTAINER_ID} .cgt-mon-silence, #${CONTAINER_ID} .cgt-mon-close {
        background: #1a1a1a; color: #f5f5f7;
        border: 0.5px solid rgba(255,255,255,0.15);
        border-radius: 6px; padding: 6px 12px;
        font-family: inherit; font-size: 11.5px; font-weight: 600; cursor: pointer;
      }
      #${CONTAINER_ID} .cgt-mon-silence:hover, #${CONTAINER_ID} .cgt-mon-close:hover { background: #2a2a2a; }
      #${CONTAINER_ID} .cgt-mon-silence:disabled { opacity: 0.5; cursor: not-allowed; }
      #${CONTAINER_ID} .cgt-mon-close { color: #2997FF; border-color: rgba(41,151,255,0.35); }

      /* Grid principal estilo monitor hospitalario real: waveforms a la
         izquierda (ECG arriba + SpO2 abajo), valores numéricos a la derecha
         (HR + SpO2 + NIBP + RR + TEMP) como columnas uniformes. */
      #${CONTAINER_ID} .cgt-mon-grid {
        display: grid; flex: 1; min-height: 0; gap: 2px;
        padding: 2px; background: #0a0a0a;
        grid-template-columns: 3fr 1fr 1fr;
        grid-template-rows: 1fr 1fr 1fr;
        grid-template-areas:
          "ecg   hr    spo2"
          "pleth nibp  rr"
          "pleth temp  rr";
      }
      #${CONTAINER_ID} .cgt-mon-ecg { grid-area: ecg; }
      #${CONTAINER_ID} .cgt-mon-pleth { grid-area: pleth; }
      #${CONTAINER_ID} .cgt-mon-hr { grid-area: hr; }
      #${CONTAINER_ID} .cgt-mon-spo2 { grid-area: spo2; }
      #${CONTAINER_ID} .cgt-mon-nibp { grid-area: nibp; }
      #${CONTAINER_ID} .cgt-mon-rr { grid-area: rr; }
      #${CONTAINER_ID} .cgt-mon-temp { grid-area: temp; }

      @media (max-width: 900px) {
        #${CONTAINER_ID} .cgt-mon-grid {
          grid-template-columns: 1fr 1fr;
          grid-template-rows: 2fr 2fr 1fr 1fr 1fr;
          grid-template-areas:
            "ecg   ecg"
            "pleth pleth"
            "hr    spo2"
            "nibp  rr"
            "nibp  temp";
        }
      }

      #${CONTAINER_ID} .cgt-mon-waveform {
        position: relative;
        background: #000;
        border: 0.5px solid rgba(255,255,255,0.06);
      }
      #${CONTAINER_ID} canvas {
        width: 100%; height: 100%; display: block;
      }
      #${CONTAINER_ID} .cgt-mon-label {
        position: absolute; top: 6px; left: 10px;
        font-size: 10.5px; font-weight: 700; letter-spacing: 1.5px;
        color: rgba(255,255,255,0.65); z-index: 2;
      }
      #${CONTAINER_ID} .cgt-mon-label-title { color: #30D158; }
      #${CONTAINER_ID} .cgt-mon-pleth .cgt-mon-label-title { color: #2997FF; }
      #${CONTAINER_ID} .cgt-mon-label-sub {
        display: block; font-size: 9px; color: rgba(255,255,255,0.4);
        font-weight: 500; margin-top: 2px;
      }

      #${CONTAINER_ID} .cgt-mon-value {
        background: #000;
        border: 0.5px solid rgba(255,255,255,0.08);
        padding: 10px 14px;
        display: flex; flex-direction: column; justify-content: center;
        overflow: hidden;
      }
      #${CONTAINER_ID} .cgt-mon-value-head {
        display: flex; justify-content: space-between; align-items: baseline;
      }
      #${CONTAINER_ID} .cgt-mon-v-label {
        font-size: 11px; font-weight: 700; letter-spacing: 1.2px;
        color: rgba(255,255,255,0.65);
      }
      #${CONTAINER_ID} .cgt-mon-v-unit {
        font-size: 10px; color: rgba(255,255,255,0.35);
        font-weight: 500;
      }
      #${CONTAINER_ID} .cgt-mon-v-num {
        font-size: clamp(42px, 8vw, 88px);
        font-weight: 800; line-height: 1;
        font-family: 'JetBrains Mono', monospace;
        letter-spacing: -2px;
        margin: 4px 0;
        font-variant-numeric: tabular-nums;
      }
      #${CONTAINER_ID} .cgt-mon-v-ref {
        font-size: 10px; color: rgba(255,255,255,0.35);
        letter-spacing: 0.5px;
      }

      /* Color por vital */
      #${CONTAINER_ID} .cgt-mon-hr .cgt-mon-v-num { color: #30D158; }
      #${CONTAINER_ID} .cgt-mon-spo2 .cgt-mon-v-num { color: #2997FF; }
      #${CONTAINER_ID} .cgt-mon-nibp .cgt-mon-v-num {
        color: #FF453A; font-size: clamp(32px, 4.5vw, 54px); letter-spacing: -1px;
      }
      #${CONTAINER_ID} .cgt-mon-rr .cgt-mon-v-num { color: #FFD60A; }
      #${CONTAINER_ID} .cgt-mon-temp .cgt-mon-v-num { color: #F5F5F7; font-size: clamp(32px, 6vw, 66px); }

      /* Alarmas: pulso rojo crítico / ámbar warning */
      #${CONTAINER_ID} .cgt-mon-alarm-crit {
        animation: cgt-mon-pulse-crit 0.9s ease-in-out infinite;
        border-color: #FF453A !important;
      }
      #${CONTAINER_ID} .cgt-mon-alarm-warn {
        border-color: #FF9F0A !important;
      }
      @keyframes cgt-mon-pulse-crit {
        0%, 100% { box-shadow: inset 0 0 0 1px #FF453A; }
        50% { box-shadow: inset 0 0 0 4px #FF453A; background: rgba(255,69,58,0.15); }
      }

      #${CONTAINER_ID} .cgt-mon-action {
        background: rgba(191,90,242,0.15); color: #BF5AF2;
        border: 0.5px solid rgba(191,90,242,0.35);
        border-radius: 6px; padding: 6px 12px;
        font-family: inherit; font-size: 11.5px; font-weight: 600; cursor: pointer;
      }
      #${CONTAINER_ID} .cgt-mon-action:hover:not(:disabled) { background: rgba(191,90,242,0.25); }
      #${CONTAINER_ID} .cgt-mon-action:disabled { opacity: 0.5; cursor: not-allowed; }
      #${CONTAINER_ID} .cgt-mon-voice {
        background: rgba(48,209,88,0.15); color: #30D158;
        border-color: rgba(48,209,88,0.4);
        min-width: 130px; text-align: center;
        transition: background 0.2s, color 0.2s;
        user-select: none;
      }
      #${CONTAINER_ID} .cgt-mon-voice:hover:not(:disabled) { background: rgba(48,209,88,0.28); }
      #${CONTAINER_ID} .cgt-mon-voice-listening {
        background: rgba(255,69,58,0.22) !important;
        color: #FF6961 !important;
        border-color: rgba(255,69,58,0.55) !important;
        animation: cgt-mon-pulse-voice 0.8s ease-in-out infinite;
      }
      #${CONTAINER_ID} .cgt-mon-voice-thinking {
        background: rgba(255,159,10,0.22) !important;
        color: #FFD60A !important;
      }
      #${CONTAINER_ID} .cgt-mon-voice-speaking {
        background: rgba(41,151,255,0.22) !important;
        color: #2997FF !important;
      }
      @keyframes cgt-mon-pulse-voice {
        50% { box-shadow: 0 0 20px rgba(255,69,58,0.5); }
      }

      /* Overlay de progreso del gesto de palma (palm-hold) */
      #${CONTAINER_ID} .cgt-mon-palm-hint {
        position: absolute; top: 80px; left: 50%; transform: translateX(-50%);
        z-index: 100;
        background: rgba(191,90,242,0.15); color: #BF5AF2;
        border: 0.5px solid rgba(191,90,242,0.4);
        border-radius: 10px; padding: 10px 18px;
        display: flex; align-items: center; gap: 12px;
        font-family: inherit; font-size: 12.5px;
        opacity: 0; transition: opacity 0.2s;
        pointer-events: none;
      }
      #${CONTAINER_ID} .cgt-mon-palm-icon { font-size: 20px; }
      #${CONTAINER_ID} .cgt-mon-palm-text strong { color: #F5F5F7; }
      #${CONTAINER_ID} .cgt-mon-palm-bar {
        width: 100px; height: 4px; background: rgba(255,255,255,0.1);
        border-radius: 2px; overflow: hidden;
      }
      #${CONTAINER_ID} .cgt-mon-palm-fill {
        height: 100%; background: #BF5AF2; transition: width 0.08s linear;
      }

      /* Panel de auscultación resultante del gesto */
      #${CONTAINER_ID} .cgt-mon-auscult {
        position: absolute; top: 80px; right: 24px;
        max-width: 340px; z-index: 110;
        opacity: 0; transform: translateX(20px);
        transition: opacity 0.3s, transform 0.3s;
        pointer-events: none;
      }
      #${CONTAINER_ID} .cgt-mon-auscult-visible { opacity: 1; transform: translateX(0); }
      #${CONTAINER_ID} .cgt-mon-auscult-card {
        background: linear-gradient(135deg, rgba(48,209,88,0.12), rgba(41,151,255,0.12));
        border: 0.5px solid rgba(48,209,88,0.4);
        border-radius: 12px; padding: 14px 16px;
        color: #f5f5f7; font-family: -apple-system, 'Inter', sans-serif;
        box-shadow: 0 12px 36px rgba(0,0,0,0.5);
      }
      #${CONTAINER_ID} .cgt-mon-auscult-head {
        display: flex; justify-content: space-between; align-items: baseline;
        margin-bottom: 6px;
      }
      #${CONTAINER_ID} .cgt-mon-auscult-head span {
        font-size: 12px; font-weight: 700; color: #30D158;
        letter-spacing: 0.5px;
      }
      #${CONTAINER_ID} .cgt-mon-auscult-head small {
        font-size: 10px; color: #8e8e93; font-weight: 500;
      }
      #${CONTAINER_ID} .cgt-mon-auscult-card p {
        font-size: 13px; color: #d1d1d6; line-height: 1.5; margin: 0;
      }

      #${CONTAINER_ID} .cgt-mon-alarmbar {
        padding: 10px 16px; background: #0a0a0a;
        border-top: 0.5px solid rgba(255,255,255,0.1);
        font-size: 12px; color: #8e8e93; font-weight: 600;
        letter-spacing: 0.5px; flex-shrink: 0;
      }
      #${CONTAINER_ID} .cgt-mon-bar-warn {
        background: rgba(255,159,10,0.15); color: #FFD60A;
      }
      #${CONTAINER_ID} .cgt-mon-bar-crit {
        background: #FF453A; color: #fff;
        animation: cgt-mon-blink-bar 0.6s ease-in-out infinite;
      }
      @keyframes cgt-mon-blink-bar {
        0%, 100% { background: #FF453A; }
        50% { background: #B02020; }
      }

      /* ─────── HUD de detección de gestos clínicos (Gap 4) ─────── */
      #${CONTAINER_ID} .cgt-mon-gesthud {
        position: absolute; top: 80px; right: 12px; z-index: 20;
        background: rgba(20,20,20,0.85); backdrop-filter: blur(10px);
        border: 0.5px solid rgba(255,255,255,0.08); border-radius: 8px;
        padding: 8px 10px; min-width: 200px;
        opacity: 0; pointer-events: none;
        transition: opacity 200ms ease;
      }
      #${CONTAINER_ID} .cgt-mon-gesthud-active { opacity: 1; }
      #${CONTAINER_ID} .cgt-mon-gesthud-row {
        display: grid; grid-template-columns: 18px 1fr 80px;
        align-items: center; gap: 8px; margin-bottom: 4px;
      }
      #${CONTAINER_ID} .cgt-mon-gesthud-row:last-child { margin-bottom: 0; }
      #${CONTAINER_ID} .cgt-mon-gesthud-icon { font-size: 14px; }
      #${CONTAINER_ID} .cgt-mon-gesthud-label {
        font-size: 10px; color: #c7c7cc; font-weight: 600;
        font-family: 'JetBrains Mono', monospace;
      }
      #${CONTAINER_ID} .cgt-mon-gesthud-label em {
        font-style: normal; color: #FFD60A;
      }
      #${CONTAINER_ID} .cgt-mon-gesthud-bar {
        height: 6px; background: rgba(255,255,255,0.08);
        border-radius: 3px; overflow: hidden;
      }
      #${CONTAINER_ID} .cgt-mon-gesthud-fill {
        height: 100%; width: 0%;
        background: linear-gradient(90deg, #2997FF, #30D158);
        transition: width 120ms linear;
      }

      /* ─────── Selector de sensibilidad de gestos (Sprint 5 calibración) ─────── */
      #${CONTAINER_ID} .cgt-mon-sens {
        background: rgba(255,255,255,0.06); color: #f5f5f7;
        border: 0.5px solid rgba(255,255,255,0.12);
        padding: 6px 10px; border-radius: 6px;
        font-size: 11px; font-family: -apple-system, 'Inter', sans-serif;
        cursor: pointer;
      }
      #${CONTAINER_ID} .cgt-mon-sens:hover { background: rgba(255,255,255,0.1); }
      #${CONTAINER_ID} .cgt-mon-sens:focus { outline: 1px solid #2997FF; outline-offset: 1px; }

      /* ─────── Drug panel (Sprint 4) ─────── */
      #${CONTAINER_ID} .cgt-mon-drugs {
        padding: 12px 14px;
        border-top: 0.5px solid rgba(255,255,255,0.08);
      }
      #${CONTAINER_ID} .cgt-mon-drugs h3 {
        font-size: 10px; font-weight: 700; color: #FFD60A;
        text-transform: uppercase; letter-spacing: 1px;
        margin: 0 0 8px 0;
      }
      #${CONTAINER_ID} .cgt-mon-drugs-grid {
        display: grid; grid-template-columns: 1fr 1fr; gap: 6px;
      }
      #${CONTAINER_ID} .cgt-mon-drugs-grid button {
        background: linear-gradient(180deg, rgba(255,214,10,0.08), rgba(255,214,10,0.03));
        border: 0.5px solid rgba(255,214,10,0.25);
        color: #f5f5f7;
        padding: 8px 10px; border-radius: 8px;
        display: flex; flex-direction: column; align-items: flex-start; gap: 2px;
        text-align: left; cursor: pointer;
        font-family: -apple-system, 'Inter', sans-serif;
        transition: background 120ms, border-color 120ms, transform 120ms;
      }
      #${CONTAINER_ID} .cgt-mon-drugs-grid button:hover {
        background: rgba(255,214,10,0.15);
        border-color: rgba(255,214,10,0.55);
        transform: translateY(-1px);
      }
      #${CONTAINER_ID} .cgt-mon-drugs-grid button:active { transform: translateY(0); }
      #${CONTAINER_ID} .cgt-mon-drug-name {
        font-size: 11px; font-weight: 600; line-height: 1.2;
      }
      #${CONTAINER_ID} .cgt-mon-drug-route {
        font-size: 9px; color: #8e8e93; letter-spacing: 0.4px;
        text-transform: uppercase;
      }

      /* ─────── Flash de gesto clínico detectado (Sprint 3) ─────── */
      #${CONTAINER_ID}.cgt-mon-flash-info {
        box-shadow: inset 0 0 0 4px #2997FF, 0 0 40px rgba(41,151,255,0.4);
        transition: box-shadow 120ms ease-out;
      }
      #${CONTAINER_ID}.cgt-mon-flash-crit {
        box-shadow: inset 0 0 0 4px #FFD60A, 0 0 60px rgba(255,214,10,0.6);
        transition: box-shadow 120ms ease-out;
      }

      /* ─────── Mapa corporal de auscultación (Sprint 2) ─────── */
      #${CONTAINER_ID} .cgt-mon-auscmap {
        position: absolute; inset: 0;
        background: rgba(0,0,0,0.78); backdrop-filter: blur(8px);
        display: flex; align-items: center; justify-content: center;
        z-index: 3000; padding: 24px;
        animation: cgt-mon-auscmap-in 180ms ease-out;
      }
      @keyframes cgt-mon-auscmap-in {
        from { opacity: 0; transform: scale(0.97); }
        to   { opacity: 1; transform: scale(1); }
      }
      #${CONTAINER_ID} .cgt-mon-auscmap-card {
        background: linear-gradient(135deg, #141414, #1c1c1e);
        border: 0.5px solid rgba(48,209,88,0.4);
        border-radius: 16px;
        width: min(540px, 92vw);
        max-height: 92vh; overflow: hidden;
        display: flex; flex-direction: column;
        color: #f5f5f7; font-family: -apple-system, 'Inter', sans-serif;
        box-shadow: 0 24px 60px rgba(0,0,0,0.6);
      }
      #${CONTAINER_ID} .cgt-mon-auscmap-card header {
        display: flex; align-items: center; justify-content: space-between;
        padding: 14px 18px;
        border-bottom: 0.5px solid rgba(255,255,255,0.08);
        font-size: 13px; font-weight: 700; letter-spacing: 0.3px;
        color: #30D158;
      }
      #${CONTAINER_ID} .cgt-mon-auscmap-card header button {
        background: transparent; border: none; color: #8e8e93;
        font-size: 22px; line-height: 1; cursor: pointer;
        width: 28px; height: 28px; border-radius: 6px;
        transition: background 120ms, color 120ms;
      }
      #${CONTAINER_ID} .cgt-mon-auscmap-card header button:hover {
        background: rgba(255,69,58,0.15); color: #FF453A;
      }
      #${CONTAINER_ID} .cgt-mon-auscmap-body {
        display: grid; grid-template-columns: 1fr 220px; gap: 16px;
        padding: 18px; overflow: auto;
      }
      @media (max-width: 620px) {
        #${CONTAINER_ID} .cgt-mon-auscmap-body { grid-template-columns: 1fr; }
      }
      #${CONTAINER_ID} .cgt-mon-auscmap-svg {
        width: 100%; height: auto; max-height: 60vh;
        background: radial-gradient(circle at 50% 40%, rgba(48,209,88,0.06), transparent 60%);
        border-radius: 12px;
      }
      #${CONTAINER_ID} .cgt-mon-auscmap-zone circle {
        cursor: pointer;
        transition: transform 140ms ease, filter 140ms ease;
        transform-origin: center;
        transform-box: fill-box;
      }
      #${CONTAINER_ID} .cgt-mon-auscmap-zone text {
        pointer-events: none;
        font-family: -apple-system, 'Inter', sans-serif;
        font-weight: 700;
      }
      #${CONTAINER_ID} .cgt-mon-auscmap-zone-cardiac circle {
        fill: rgba(255,69,58,0.35);
        stroke: #FF453A; stroke-width: 0.5;
        filter: drop-shadow(0 0 2px rgba(255,69,58,0.8));
      }
      #${CONTAINER_ID} .cgt-mon-auscmap-zone-pulmonary circle {
        fill: rgba(41,151,255,0.35);
        stroke: #2997FF; stroke-width: 0.5;
        filter: drop-shadow(0 0 2px rgba(41,151,255,0.8));
      }
      #${CONTAINER_ID} .cgt-mon-auscmap-zone:hover circle {
        transform: scale(1.18);
        filter: drop-shadow(0 0 5px rgba(255,255,255,0.9));
      }
      #${CONTAINER_ID} .cgt-mon-auscmap-hint {
        font-size: 12px; line-height: 1.55; color: #a1a1a6;
        padding: 10px 12px;
        background: rgba(255,255,255,0.03);
        border: 0.5px solid rgba(255,255,255,0.06);
        border-radius: 10px;
        align-self: start;
      }
    `;
    document.head.appendChild(style);
  }
}

export const monitorDisplay = new MonitorDisplay();
